import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { IconButton } from '../Button';
import VipIcon from '../../Icons/VipIcons';
import MediaViewer from './MediaViewer';
import { getMediaType, getMediaUrl, getQualityUrls, getVideoSources } from './exports';
import { getCachedAspectRatio } from '../../../utils/mediaAspectCache';

/**
 * Universal Media Carousel Component
 * 
 * Features:
 * - Multi-media support (images, videos, audio)
 * - Touch/swipe navigation
 * - Keyboard navigation
 * - Dynamic aspect ratio handling
 * - Modal viewer integration
 * - Responsive design
 * - Progress indicators
 */

// Inline images use the feed-sized copy when one exists; the full-size image is only loaded when
// the viewer is opened (onMediaClick receives the original media object).
const getInlineMediaUrl = (media) =>
  (getMediaType(media) === 'image' && media.thumbnailVariants?.feed) || getMediaUrl(media);

const MediaCarousel = ({ 
  mediaItems = [],
  currentIndex = 0,
  onIndexChange,
  showNavigation = true,
  showIndicators = true,
  showPlayControls = true,
  autoHeight = true,
  className,
  onMediaClick,
  isOwnMessage = false, // Add prop for sender/receiver distinction
  ...props 
}) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0); // Track current video time
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  // Sync external index changes
  useEffect(() => {
    setActiveIndex(currentIndex);
  }, [currentIndex]);

  const currentItem = mediaItems[activeIndex];
  const isVideo = currentItem?.type?.toLowerCase() === 'video' || currentItem?.mediaType?.toLowerCase() === 'video';
  const isAudio = currentItem?.type?.toLowerCase() === 'audio' || currentItem?.mediaType?.toLowerCase() === 'audio';

  // Calculate the tallest aspect ratio from all media items (smallest ratio = tallest)
  // This prevents layout jumping when swiping between different aspect ratios
  const containerAspectRatio = useMemo(() => {
    // Stored dimensions first; otherwise a ratio remembered from an earlier load (re-mounted post)
    const ratios = mediaItems
      .map(item => (item.width && item.height ? item.width / item.height : getCachedAspectRatio(getInlineMediaUrl(item))))
      .filter(Boolean);
    
    if (ratios.length === 0) return null;
    
    // Return the smallest ratio (tallest container)
    return Math.min(...ratios);
  }, [mediaItems]);

  // Navigation handlers
  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < mediaItems.length) {
      setActiveIndex(index);
      setIsPlaying(false); // Pause when changing slides
      onIndexChange?.(index);
    }
  }, [mediaItems.length, onIndexChange]);

  const nextSlide = useCallback(() => {
    goToSlide(activeIndex + 1);
  }, [activeIndex, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide(activeIndex - 1);
  }, [activeIndex, goToSlide]);

  // Media control handlers
  const handlePlay = useCallback(() => setIsPlaying(true), []);
  const handlePause = useCallback(() => setIsPlaying(false), []);
  const handleEnded = useCallback(() => setIsPlaying(false), []);
  
  // Track video time updates
  const handleTimeUpdate = useCallback((time) => {
    setCurrentTime(time);
  }, []);

  // Enhanced fullscreen handler that passes current video state
  const handleFullscreenClick = useCallback((media, index) => {
    if (onMediaClick) {
      // Pass the media with current playback state for videos
      const mediaWithState = {
        ...media,
        startTime: currentTime, // Current playback position
        shouldAutoPlay: isPlaying && index === activeIndex // Whether to auto-play in modal
      };
      onMediaClick(mediaWithState, index);
    }
  }, [onMediaClick, currentTime, isPlaying, activeIndex]);

  const togglePlayPause = useCallback(() => {
    if (isVideo && videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    } else if (isAudio && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  }, [isPlaying, isVideo, isAudio]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && activeIndex < mediaItems.length - 1) {
      nextSlide();
    }
    if (isRightSwipe && activeIndex > 0) {
      prevSlide();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle keyboard events if user is typing in an input field
      if (e.target.tagName === 'INPUT' || 
          e.target.tagName === 'TEXTAREA' || 
          e.target.contentEditable === 'true' ||
          e.target.closest('.ProseMirror')) {
        return;
      }
      
      if (e.key === 'ArrowLeft' && activeIndex > 0) {
        prevSlide();
      } else if (e.key === 'ArrowRight' && activeIndex < mediaItems.length - 1) {
        nextSlide();
      } else if (e.key === ' ') {
        e.preventDefault();
        togglePlayPause();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, mediaItems.length, prevSlide, nextSlide, togglePlayPause]);

  const getMediaIcon = (type) => {
    switch (type) {
      case 'video': return <VipIcon name="VideoIcon" size={12} />;
      case 'audio': return <VipIcon name="MicIcon" size={12} />;
      default: return <VipIcon name="ImageIcon" size={12} />;
    }
  };

  if (!mediaItems.length) return null;

  return (
    <CarouselContainer className={`carousel-container ${className || ''}`} {...props}>
      {/* Main Media Display */}
      <MediaContainer
        $aspectRatio={containerAspectRatio}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <MediaSlider $activeIndex={activeIndex} $totalItems={mediaItems.length}>
          {mediaItems.map((media, index) => {
            const mediaType = getMediaType(media);
            const mediaUrl = getInlineMediaUrl(media);
            const qualityUrls = getQualityUrls(media);
            const videoSources = getVideoSources(media);
            
            return (
              <MediaSlide key={index} $totalItems={mediaItems.length}>
                <MediaViewer
                  src={mediaUrl} // Fallback single URL
                  qualityUrls={qualityUrls} // Multi-quality URL mapping
                  videoSources={videoSources} // Array of VideoQuality objects
                  defaultQuality="720p" // Default quality selection. Can be dynamic based on user preference or network conditions.
                  enableQualitySelection={true} // Enable quality selection UI
                  type={mediaType}
                  alt={media.name || media.alt || `Media ${index + 1}`}
                  onClick={mediaType === 'video' ? undefined : () => onMediaClick?.(media, index)} // Let video handle its own clicks
                  onPlay={handlePlay}
                  onPause={handlePause}
                  onEnded={handleEnded}
                  onTimeUpdate={handleTimeUpdate} // Track video time
                  mediaRef={mediaType === 'video' ? videoRef : audioRef}
                  showControls={showPlayControls && (isVideo || isAudio) && index === activeIndex}
                  isPlaying={isPlaying && index === activeIndex}
                  onTogglePlay={togglePlayPause}
                  onFullscreenClick={mediaType === 'video' ? undefined : () => handleFullscreenClick(media, index)} // Only for non-videos
                  poster={media.thumbnailUrl || media.poster}
                  processingStatus={media.processingState || media.processingStatus || 'completed'}
                  isOwnMessage={isOwnMessage}
                  rejectionReason={media.rejectionReason}
                  isFreePreview={media.isFreePreview !== undefined ? media.isFreePreview : true}
                  showLockIcon={true}
                />
              </MediaSlide>
            );
          })}
        </MediaSlider>

      </MediaContainer>

      {/* Navigation Controls Below Media */}
      {showNavigation && mediaItems.length > 1 && !(isPlaying && isVideo) && (
        <NavigationContainer>
          <NavButton 
            onClick={prevSlide}
            disabled={activeIndex === 0}
            title="Previous media"
            aria-label="Previous media"
          >
            <VipIcon name="ChevronLeft" size={20} />
          </NavButton>
          <NavButton 
            onClick={nextSlide}
            disabled={activeIndex === mediaItems.length - 1}
            title="Next media"
            aria-label="Next media"
          >
            <VipIcon name="ChevronRight" size={20} />
          </NavButton>
        </NavigationContainer>
      )}

      {/* Apple-style Moving Progress Indicators */}
      {showIndicators && mediaItems.length > 1 && (
        <IndicatorsContainer>
          <ProgressTrack>
            {(() => {
              // Show only 2 dots: current and one adjacent (dynamic sliding window)
              const maxVisibleDots = 2;
              const totalDots = mediaItems.length;
              
              let startIndex, endIndex;
              
              if (totalDots <= maxVisibleDots) {
                // Show all dots if total is 2 or less
                startIndex = 0;
                endIndex = totalDots - 1;
              } else {
                // Show sliding window of 2 dots: current and next (or previous if at end)
                if (activeIndex === totalDots - 1) {
                  // At the end, show previous and current
                  startIndex = activeIndex - 1;
                  endIndex = activeIndex;
                } else {
                  // Show current and next
                  startIndex = activeIndex;
                  endIndex = activeIndex + 1;
                }
              }
              
              const visibleDots = [];
              
              for (let i = startIndex; i <= endIndex; i++) {
                const isFirst = i === startIndex;
                const isLast = i === endIndex;
                const media = mediaItems[i];
                
                visibleDots.push(
                  <React.Fragment key={i}>
                    {/* Connecting line before dot (except for first visible dot) */}
                    {!isFirst && (
                      <ConnectingLine
                        $filled={i <= activeIndex}
                      />
                    )}
                    
                    {/* Progress dot */}
                    <ProgressDot
                      $active={i <= activeIndex}
                      $current={i === activeIndex}
                      onClick={() => goToSlide(i)}
                      title={`${getMediaType(media).charAt(0).toUpperCase() + getMediaType(media).slice(1)} ${i + 1} of ${mediaItems.length}`}
                    >
                      <DotIcon $current={i === activeIndex}>
                        {getMediaIcon(getMediaType(media))}
                      </DotIcon>
                    </ProgressDot>
                    
                    {/* Connecting line after dot (except for last visible dot) */}
                    {!isLast && (
                      <ConnectingLine
                        $filled={i < activeIndex}
                      />
                    )}
                  </React.Fragment>
                );
              }
              
              return visibleDots;
            })()}
          </ProgressTrack>
          
          {/* Progress counter */}
          <ProgressCounter>
            {activeIndex + 1}/{mediaItems.length}
          </ProgressCounter>
        </IndicatorsContainer>
      )}
    </CarouselContainer>
  );
};

// Styled Components
const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  background: ${({ theme }) => theme.colors.cardBg || '#fff'};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  
  /* Show navigation on hover */
  &:hover button {
    opacity: 0.8;
  }
`;

const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  /* Dynamic aspect ratio based on content */
  ${({ $aspectRatio }) => $aspectRatio ? `
    aspect-ratio: ${$aspectRatio};
  ` : `
    aspect-ratio: 9 / 16; /* Default fallback for vertical */
  `}
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bg2 || '#f8f9fa'};
  border-radius: 12px;
  display: block;
  
  &:hover {
    button {
      opacity: 1;
    }
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.cardBg || '#fff'};
  border-top: 1px solid ${({ theme }) => theme.colors.border || '#e1e5e9'};
  
  @media (max-width: 768px) {
    padding: 10px 12px;
  }
`;

const MediaSlider = styled.div`
  display: flex;
  width: ${({ $totalItems }) => $totalItems * 100}%;
  height: 100%;
  transform: translateX(-${({ $activeIndex, $totalItems }) => 
    $activeIndex * (100 / $totalItems)}%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-wrap: nowrap;
`;

const MediaSlide = styled.div`
  width: ${({ $totalItems = 1 }) => 100 / $totalItems}%;
  height: 100%;
  flex-shrink: 0;
  flex-grow: 0;
  flex-basis: ${({ $totalItems = 1 }) => 100 / $totalItems}%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: visible; /* Allow content to extend for controls */
`;

const NavButton = styled(IconButton).attrs({ $variant: 'primary', $size: 'small' })`
  pointer-events: ${({ disabled }) => disabled ? 'none' : 'auto'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.3;
    background: ${({ theme }) => theme.colors.disabled || '#ccc'};
  }
`;

const IndicatorsContainer = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding: 8px 16px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 10; /* Higher z-index to appear above media navigation buttons */
  max-width: calc(100% - 280px); /* Leave more space for navigation buttons */
  min-width: fit-content;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    bottom: 8px;
    padding: 8px 16px;
    border-radius: 16px;
    gap: 6px;
    max-width: calc(100% - 200px); /* More space for navigation buttons on tablet */
  }
  
  @media (max-width: 480px) {
    bottom: 8px;
    padding: 8px 16px;
    border-radius: 16px;
    gap: 6px;
    max-width: calc(100% - 180px); /* More space for navigation buttons on mobile */
  }
`;

const ProgressTrack = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ConnectingLine = styled.div`
  width: 8px;
  height: 1.5px;
  background: ${({ $filled, theme }) => 
    $filled 
      ? (theme.colors.primary || '#007bff')
      : 'rgba(255, 255, 255, 0.3)'
  };
  border-radius: 1px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0 1px;
  
  @media (max-width: 768px) {
    width: 6px;
    height: 1px;
    margin: 0 0.5px;
  }
  
  @media (max-width: 480px) {
    width: 4px;
    height: 1px;
    margin: 0 0.3px;
  }
`;

const ProgressDot = styled.div`
  width: ${({ $current }) => $current ? '16px' : '10px'};
  height: ${({ $current }) => $current ? '16px' : '10px'};
  border-radius: 50%;
  background: ${({ $active, $current, theme }) => {
    if ($current) return theme.colors.primary || '#007bff';
    if ($active) return theme.colors.primary || '#007bff';
    return 'rgba(255, 255, 255, 0.4)';
  }};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 1px;
  
  /* Subtle glow effect for current dot */
  ${({ $current, theme }) => $current && `
    box-shadow: 
      0 0 0 1px rgba(255, 255, 255, 0.2),
      0 0 8px ${theme.colors.primary || '#007bff'}40;
  `}
  
  &:hover {
    transform: scale(${({ $current }) => $current ? '1.1' : '1.2'});
    background: ${({ theme }) => theme.colors.primary || '#007bff'};
  }
  
  @media (max-width: 768px) {
    width: ${({ $current }) => $current ? '14px' : '8px'};
    height: ${({ $current }) => $current ? '14px' : '8px'};
    margin: 0 0.5px;
  }
  
  @media (max-width: 480px) {
    width: ${({ $current }) => $current ? '12px' : '7px'};
    height: ${({ $current }) => $current ? '12px' : '7px'};
    margin: 0 0.3px;
  }
`;

const DotIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $current }) => $current ? 'white' : 'rgba(255, 255, 255, 0.8)'};
  transition: all 0.3s ease;
  
  svg {
    width: ${({ $current }) => $current ? '7px' : '5px'};
    height: ${({ $current }) => $current ? '7px' : '5px'};
    transition: all 0.3s ease;
    
    @media (max-width: 768px) {
      width: ${({ $current }) => $current ? '6px' : '4px'};
      height: ${({ $current }) => $current ? '6px' : '4px'};
    }
    
    @media (max-width: 480px) {
      width: ${({ $current }) => $current ? '5px' : '3px'};
      height: ${({ $current }) => $current ? '5px' : '3px'};
    }
  }
`;

const ProgressCounter = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 11px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  text-align: center;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 9px;
  }
`;

export default MediaCarousel;
